import { IndicatorType, SloDTO, SloDTOFromJSON } from '@atlassiansox/phobos-client-ts';
import React, { FC, Fragment, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import EmptyState from '@atlaskit/empty-state';

import { apiFetch } from '@/services';
import {
  sortSlos,
  useFetchErrorController,
  useLocationController,
  getSLOFilterQuery,
  filterStore,
} from '@/controllers';
import { SLOCardViewType, SearchConfigContext } from '@/controllers/search-config-controller';
import { MiniSLOCard } from '@/components/composed';
import { querySpec } from '@/services/api-service/api-queries';
import { mapParamsToAPIQuery } from '@/components/composed/catalog-search-bar/services/utils';
import { Params } from '@/components/composed/catalog-search-bar/types';
import GroupCard from '@/components/composed/slo-card/group-card';

import SearchPageSkeleton from '../search-page-skeleton';

import * as styles from './styles';

type SearchListCapabilityCardProps = {
  id: string;
};

const SearchListCapabilityCard: FC<SearchListCapabilityCardProps> = ({ id }) => {
  const { onError } = useFetchErrorController();
  const { isSearchPage } = useLocationController();
  const { state: filterState } = useContext(filterStore);

  const capabilityFilter = isSearchPage ? '' : getSLOFilterQuery(filterState.slo);
  // Note that we pass "false" to not let API sort SLOs, as we are sorting the
  // mini SLO cards in Front-End for some historical reason. This is not a good
  // experience for the user since the order of mini cards becomes different
  // from the order in the capability page. In the future, we should consider
  // remove all ".sort(sortSlos)" from Front-End and change second param here to
  // true, which essentially passes sorting params to API.
  const capabilityParams = mapParamsToAPIQuery({} as Params, false);

  const { queryKey, path } = querySpec('capability.{id}.slos-spark.{params}', {
    id,
    params: `${capabilityParams}${capabilityFilter}`,
  });
  const { data, isInitialLoading: isLoading } = useQuery(queryKey, () => apiFetch(path), {
    onError,
  });

  const slos: SloDTO[] = (data?.content ?? []).map(SloDTOFromJSON).sort(sortSlos);
  const hasSLOs: boolean = slos.length > 0;

  const { sloCardViewType } = useContext(SearchConfigContext);
  const isSLOCardListView: boolean = sloCardViewType === SLOCardViewType.ListView;

  if (isLoading)
    return (
      <div css={styles.cardSkeletonStyles}>
        <SearchPageSkeleton isCompact />
      </div>
    );

  return (
    <div css={styles.cardCapabilityContentStyles}>
      {!hasSLOs && <EmptyState header="" description="No SLOs found for the selected search" />}
      {hasSLOs && (
        <div
          css={[
            styles.capabilityCardStyles,
            isSLOCardListView ? styles.capabilityCardExtendingStyles : '',
          ]}
        >
          {slos.map(slo => {
            const isGroup: boolean = slo.indicator?.type === IndicatorType.Grouped;
            return (
              <Fragment key={slo.publicId}>
                {isGroup && (
                  <GroupCard
                    {...slo}
                    isCompact={!isSLOCardListView}
                    isExtending={isSLOCardListView}
                  />
                )}
                {!isGroup && (
                  <MiniSLOCard
                    {...slo}
                    capabilityId={id}
                    isCompact={!isSLOCardListView}
                    isExtending={isSLOCardListView}
                  />
                )}
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SearchListCapabilityCard;
